/*
 * How the batch-flow endpoints answer when they do not answer 200
 * (contracts README "Conventions", error catalogue § 1).
 *
 *   400 → { reason, subReason?, message, details, kind } with kind "refusal" or
 *         "warning". A warning is not a failure: the screen shows it and resends
 *         the same request with the id added to `acceptedWarnings`.
 *   500 → { error: true, message, reference }; the message already names the
 *         reference, which is in the backend's logs.
 *
 * `useFetch` rejects with the axios error, so the body sits at
 * `error.response.data`. Anything else (a network failure, a timeout) has no
 * body and reads as a generic failure.
 */

/** The refusals the two screens react to by name, not only by message. */
export const REASONS = {
  SCHEDULE_CONFLICTS: "schedule-conflicts",
  MENTOR_OCCUPIED: "mentor-occupied",
  FEATURE_OFF: "feature-off",
  BATCH_NOT_FOUND: "batch-not-found",
  BATCH_EMPTY: "batch-empty",
  VALIDITY_OVERRUN: "validity-overrun",
  STITCH_OUT_OF_SEQUENCE: "stitch-out-of-sequence",
  NO_ACCOUNT_OWNER: "no-account-owner",
  STUDENT_NOT_ELIGIBLE: "student-not-eligible",
};

/*
 * The backend calls the parent's `csmId` the "account owner"; the ops team calls
 * that person the student manager, and so does every label on these screens. The
 * refusal is reworded here, once, rather than on each screen that can receive it
 * — verify answers it directly, the create wraps it as `student-not-eligible`.
 */
const NO_STUDENT_MANAGER_MESSAGES = {
  none: "This student doesn't have a student manager yet. Add one on the student's profile, then verify again.",
  "login-inactive":
    "This student's manager no longer has an active login. Choose a new student manager on the student's profile, then verify again.",
};

/**
 * Whether a refusal is the missing (or inactive) student manager, however it was
 * wrapped.
 * @param {{reason: string|null, subReason: string|null}} envelope
 * @returns {boolean}
 */
export const isNoStudentManager = (envelope) =>
  envelope?.reason === REASONS?.NO_ACCOUNT_OWNER ||
  (envelope?.reason === REASONS?.STUDENT_NOT_ELIGIBLE && envelope?.subReason === REASONS?.NO_ACCOUNT_OWNER);

/* A message the backend composed from a name it holds (the student's) is kept, and
   only the wrong term in it is swapped — for any row not reworded above. */
const withStudentManagerTerm = (message) =>
  message?.replace?.(/account owner/g, "student manager")?.replace?.(/Account owner/g, "Student manager");

const GENERIC_MESSAGE = "Something went wrong. Please try again.";
/* A write is one transaction: when one fails, nothing of it was applied. */
export const GENERIC_WRITE_MESSAGE = "Something went wrong and nothing was saved. Please try again.";

/**
 * @typedef {Object} EnvelopeVM
 * @property {number|null} status - HTTP status, null without a response
 * @property {"refusal"|"warning"|"error"} kind
 * @property {string|null} reason
 * @property {string|null} subReason
 * @property {string} message - written for the ops user; shown as it comes
 * @property {Object} details
 * @property {string|null} reference - on a 500
 */

/**
 * A rejected batch-flow call → EnvelopeVM.
 * @param {object} error - what `useFetch`'s mutate/refetch rejected with
 * @param {string} [fallbackMessage] - shown when the backend sent no message
 * @returns {EnvelopeVM}
 */
export const readEnvelope = (error, fallbackMessage = GENERIC_MESSAGE) => {
  const status = error?.response?.status ?? null;
  const body = error?.response?.data ?? {};
  const isEnvelope = status === 400 && !!body?.reason;
  const reason = isEnvelope ? body?.reason : null;
  const subReason = isEnvelope ? (body?.subReason ?? null) : null;
  /* A wrapped refusal carries the inner row's own sub-reason only in its message,
     so an unknown one falls back to the plain "none" wording. */
  const managerMessage = isNoStudentManager({ reason, subReason })
    ? (NO_STUDENT_MANAGER_MESSAGES?.[subReason] ?? NO_STUDENT_MANAGER_MESSAGES?.none)
    : "";

  return {
    status,
    kind: isEnvelope ? (body?.kind === "warning" ? "warning" : "refusal") : "error",
    reason,
    subReason,
    message: managerMessage || withStudentManagerTerm(body?.message) || fallbackMessage,
    details: body?.details ?? {},
    reference: body?.reference ?? null,
  };
};

/**
 * The next `acceptedWarnings` list after the ops user confirms a warning. The
 * backend answers one warning per round trip, so this grows by one each time.
 * @param {string[]} accepted
 * @param {string} warningId
 * @returns {string[]}
 */
export const acceptWarning = (accepted, warningId) =>
  accepted?.includes(warningId) ? accepted : [...(accepted ?? []), warningId];

/* Warnings accepted per batch, as `<reason>:<details.batchId>` (F11, F12). */
const PER_BATCH_WARNINGS = [REASONS?.VALIDITY_OVERRUN, REASONS?.STITCH_OUT_OF_SEQUENCE];

/**
 * What a warning is accepted AS. The validity overrun and a stitch out of sequence
 * are accepted per batch, so a chain's head and its successor each need their own
 * round trip and their own entry; every other warning is accepted once for the
 * whole request.
 *
 * The backend builds the same string and compares it exactly, so the id it gave in
 * `details.batchId` is echoed verbatim — never rebuilt from the batch name on screen.
 * @param {EnvelopeVM} envelope
 * @returns {string} the entry to add to `acceptedWarnings`
 */
export const warningAcceptId = (envelope) =>
  PER_BATCH_WARNINGS?.includes(envelope?.reason) && envelope?.details?.batchId
    ? `${envelope?.reason}:${envelope?.details?.batchId}`
    : envelope?.reason;
